import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatUsd } from "../lib/format";
import { TradePanel } from "../components/TradePanel";
import { OrderBook } from "../components/OrderBook";
import { PriceChart } from "../components/PriceChart";
import { MarketCard } from "../components/MarketCard";
import { MarketCover } from "../components/MarketCover";
import { useAuth } from "../store/auth";
import type { Market } from "../components/MarketCard";

type Detail = {
  market: Market & {
    description: string;
    resolution_source: string;
    resolution_rules: string;
    resolution_methodology: string;
    resolution_date: string;
    closes_at: string;
    open_interest_shares: number;
    volume_24h_micro: string | number;
    trader_count: number;
    liquidity_label: string;
    category_name: string;
    category_color?: string;
    category_icon?: string;
    event_slug?: string | null;
    event_title?: string | null;
  };
  sources: { name: string }[];
  events: { type: string; title: string; created_at: string }[];
  watching: boolean;
  related?: Array<Market & { event_slug?: string | null; event_title?: string | null; category_color?: string; category_icon?: string }>;
};

export function MarketPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("chart");
  const [sheet, setSheet] = useState(false);
  const [comment, setComment] = useState("");
  const q = useQuery({
    queryKey: ["market", slug],
    queryFn: () => api<Detail>(`/markets/${slug}`),
  });
  const m = q.data?.market;
  const book = useQuery({
    queryKey: ["book", m?.id],
    queryFn: () => api<Parameters<typeof OrderBook>[0]["book"]>(`/markets/${m!.id}/orderbook`),
    enabled: !!m,
    refetchInterval: 4000,
  });
  const trades = useQuery({
    queryKey: ["trades", m?.id],
    queryFn: () => api<{ trades: { id: string; price_cents: number; quantity_shares: number; created_at: string; fill_type: string; maker_is_system: number }[] }>(`/markets/${m!.id}/trades`),
    enabled: !!m,
  });
  const chart = useQuery({
    queryKey: ["chart", m?.id],
    queryFn: () => api<{ ticks: { ts: string; yes_price_cents: number }[] }>(`/markets/${m!.id}/chart?range=7D`),
    enabled: !!m,
  });
  const comments = useQuery({
    queryKey: ["comments", m?.id],
    queryFn: () => api<{ comments: { id: string; body: string; username: string; created_at: string }[] }>(`/markets/${m!.id}/comments`),
    enabled: !!m,
  });

  useEffect(() => {
    if (!m) return;
    const es = new EventSource(`/events/stream?channels=${encodeURIComponent(`market.${m.id},orderbook.${m.id},trades.${m.id}`)}`);
    es.addEventListener("message", () => {
      void qc.invalidateQueries({ queryKey: ["book", m.id] });
      void qc.invalidateQueries({ queryKey: ["trades", m.id] });
      void qc.invalidateQueries({ queryKey: ["market", slug] });
    });
    return () => es.close();
  }, [m?.id, qc, slug]);

  const watch = useMutation({
    mutationFn: () => api(`/markets/${m!.id}/watch`, { method: q.data?.watching ? "DELETE" : "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["market", slug] }),
  });
  const post = useMutation({
    mutationFn: () => api(`/markets/${m!.id}/comments`, { method: "POST", body: JSON.stringify({ body: comment }) }),
    onSuccess: () => { setComment(""); void qc.invalidateQueries({ queryKey: ["comments", m?.id] }); },
  });

  if (q.isLoading) return <div className="wrap"><div className="skeleton" style={{ height: 28 }} /></div>;
  if (!m) return <div className="wrap empty"><h3>Market not found.</h3></div>;

  const panel = <div className="card"><h3 style={{ marginTop: 0 }}>Trade</h3><TradePanel marketId={m.id} yes={m.yes_price_cents} no={m.no_price_cents} /></div>;

  return (
    <>
    <div className="wrap market-page">
      <div>
        <div className="mkt-meta">
          <span className="kicker" style={{ marginBottom: 0 }}>{m.event_title || m.category_name}</span>
          <span className="badge badge-live">{m.status}</span>
          <span>{m.timeRemaining}</span>
        </div>
        <MarketCover
          accent={m.category_color}
          eventSlug={m.event_slug}
          icon={m.category_icon}
          imageUrl={m.image_url}
          fit={m.image_fit}
        />
        <h1 className="market-title">{m.question}</h1>
        <div className="prob-row">
          <div className="prob-big">{m.yes_price_cents}%</div>
          <div className="yesno" style={{ flex: 1 }}>
            <div className="pill-yes"><span>YES</span><span>{m.yes_price_cents}¢</span></div>
            <div className="pill-no"><span>NO</span><span>{m.no_price_cents}¢</span></div>
          </div>
        </div>
        <div className="mkt-meta" style={{ margin: "12px 0 20px" }}>
          <span>{formatUsd(m.volume_total_micro, true)} traded</span>
          <span>{formatUsd(m.volume_24h_micro, true)} 24h</span>
          <span>{formatUsd(m.liquidity_micro, true)} bid liquidity</span>
          <span>{Number(m.open_interest_shares).toLocaleString()} OI</span>
          <span>{m.trader_count.toLocaleString()} traders</span>
          {user && <button className="btn btn-ghost" onClick={() => watch.mutate()}>{q.data?.watching ? "Watching" : "Watch"}</button>}
        </div>
        <div className="tabs">
          {["chart", "book", "trades", "rules", "discussion"].map((t) => (
            <button key={t} className={tab === t ? "on" : ""} onClick={() => setTab(t)}>{t}</button>
          ))}
        </div>
        {tab === "chart" && <div className="card"><PriceChart ticks={chart.data?.ticks ?? []} /></div>}
        {tab === "book" && book.data && <div className="card"><OrderBook book={book.data} /></div>}
        {tab === "trades" && (
          <div className="card">
            <table className="table">
              <thead><tr><th>Time</th><th>Type</th><th>Price</th><th>Size</th><th></th></tr></thead>
              <tbody>
                {(trades.data?.trades ?? []).map((t) => (
                  <tr key={t.id}>
                    <td>{new Date(t.created_at).toLocaleString()}</td>
                    <td>{t.fill_type}</td>
                    <td>{t.price_cents}¢</td>
                    <td>{t.quantity_shares}</td>
                    <td>{t.maker_is_system ? "LP" : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!trades.data?.trades.length && <p className="empty">No public prints yet. The book is still resting.</p>}
          </div>
        )}
        {tab === "rules" && (
          <div className="card">
            <p>{m.description}</p>
            <dl className="kv">
              <dt>Resolution source</dt><dd>{m.resolution_source}</dd>
              <dt>Methodology</dt><dd>{m.resolution_methodology}</dd>
              <dt>Resolution date</dt><dd>{new Date(m.resolution_date).toUTCString()}</dd>
              <dt>Closes</dt><dd>{new Date(m.closes_at).toUTCString()}</dd>
            </dl>
            <h3>Rules</h3>
            <p>{m.resolution_rules}</p>
            <p>If this result looks wrong after publication, you can <a href="/disputes">open a dispute</a>.</p>
          </div>
        )}
        {tab === "discussion" && (
          <div className="card">
            {(comments.data?.comments ?? []).map((c) => (
              <div key={c.id} style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <strong>{c.username}</strong> · <span style={{ color: "var(--muted)" }}>{new Date(c.created_at).toLocaleString()}</span>
                <p>{c.body}</p>
              </div>
            ))}
            {!(comments.data?.comments ?? []).length && (
              <p className="empty" style={{ padding: 16 }}>Be the first to talk through the resolution rules.</p>
            )}
            {user ? (
              <form onSubmit={(e) => { e.preventDefault(); post.mutate(); }}>
                <label className="field">Comment
                  <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} />
                </label>
                <button className="btn btn-primary" type="submit">Post</button>
              </form>
            ) : <p>Sign in to join the discussion.</p>}
          </div>
        )}
        {(q.data?.events ?? []).length > 0 && (
          <div className="card" style={{ marginTop: 16 }}>
            <h3>Market events</h3>
            {q.data!.events.map((e, i) => (
              <div key={i} className="mkt-meta"><span className="badge badge-navy">{e.type}</span><span>{e.title}</span></div>
            ))}
          </div>
        )}
        <div className="mobile-cta">
          <button className="btn btn-yes" onClick={() => setSheet(true)}>Buy Yes</button>
          <button className="btn btn-no" onClick={() => setSheet(true)}>Buy No</button>
        </div>
      </div>
      <aside className="sticky-panel desktop-trade">{panel}</aside>
      {sheet && (
        <>
          <div className="sheet-backdrop" onClick={() => setSheet(false)} />
          <div className="sheet">{panel}</div>
        </>
      )}
    </div>
    {(q.data?.related ?? []).length > 0 && (
      <div className="wrap" style={{ paddingTop: 0 }}>
        <section>
          <div className="section-h">
            <div>
              <span className="kicker">Same event</span>
              <h2>{m.event_title || "Related contracts"}</h2>
            </div>
            {m.event_slug && (
              <Link to={`/topics/${m.event_slug}`}>View all</Link>
            )}
          </div>
          <div className="grid-3">
            {q.data!.related!.map((r) => <MarketCard key={r.id} market={r} />)}
          </div>
        </section>
      </div>
    )}
    </>
  );
}
